import React from 'react';
import { Link } from 'react-router-dom';
import './LayoutTemplate.css';

interface LayoutTemplateProps {
  // Header section
  header?: {
    logo?: string;
    title?: string;
    navigation?: Array<{
      label: string;
      href: string;
    }>;
  };
  
  // Hero/Banner section
  hero?: {
    title?: string;
    subtitle?: string;
    description?: string;
    backgroundImage?: string;
    ctaButton?: {
      text: string;
      href: string;
    };
  };
  
  // Main content sections
  sections?: Array<{
    id: string;
    type: 'text' | 'image-text' | 'gallery' | 'cards' | 'full-width-image';
    title?: string;
    content?: string;
    image?: string;
    images?: string[];
    reverse?: boolean; // for image-text layout
  }>;
  
  // Footer
  footer?: {
    text?: string;
    links?: Array<{
      label: string;
      href: string;
    }>;
  };
}

const LayoutTemplate: React.FC<LayoutTemplateProps> = ({
  hero,
  sections = [],
  footer
}) => {
  return (
    <div className="layout-template">
      {/* Hero Section */}
      {hero && (
        <section 
          className="hero-section"
          style={{
            backgroundImage: hero.backgroundImage ? `url(${hero.backgroundImage})` : undefined
          }}
        >
          <div className="hero-content">
            {hero.title && (
              <h2 className="hero-title">{hero.title}</h2>
            )}
            {hero.subtitle && (
              <h3 className="hero-subtitle">{hero.subtitle}</h3>
            )}
            {hero.description && (
              <p className="hero-description">{hero.description}</p>
            )}
            {hero.ctaButton && (
              <Link to={hero.ctaButton.href} className="cta-button">
                {hero.ctaButton.text}
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Content Sections */}
      {sections.map((section, index) => {
        switch (section.type) {
          case 'text':
            return (
              <section key={section.id} className="content-section text-section">
                <div className="container">
                  {section.title && (
                    <h2 className="section-title">{section.title}</h2>
                  )}
                  {section.content && (
                    <div className="section-content">
                      <p>{section.content}</p>
                    </div>
                  )}
                </div>
              </section>
            );

          case 'image-text':
            return (
              <section key={section.id} className="content-section image-text-section">
                <div className={`container ${section.reverse ? 'reverse' : ''}`}>
                  <div className="image-col">
                    {section.image && (
                      <img src={section.image} alt={section.title || 'Section visual'} />
                    )}
                  </div>
                  <div className="text-col">
                    {section.title && (
                      <h2 className="section-title">{section.title}</h2>
                    )}
                    {section.content && (
                      <div className="section-content">
                        <p>{section.content}</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            );

          case 'gallery':
            return (
              <section key={section.id} className="content-section gallery-section">
                <div className="container">
                  {section.title && (
                    <h2 className="section-title">{section.title}</h2>
                  )}
                  <div className="gallery-grid">
                    {section.images?.map((image, imgIndex) => (
                      <div key={imgIndex} className="gallery-item">
                        <img src={image} alt={`Gallery item ${imgIndex + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );

          case 'cards':
            return (
              <section key={section.id} className="content-section cards-section">
                <div className="container">
                  {section.title && (
                    <h2 className="section-title">{section.title}</h2>
                  )}
                  <div className="cards-grid">
                    {section.content?.split('\n').map((item, itemIndex) => (
                      <div key={itemIndex} className="card">
                        <p>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );

          case 'full-width-image':
            return (
              <section key={section.id} className="content-section full-width-image-section">
                {section.image && (
                  <img src={section.image} alt={section.title || 'Full width image'} />
                )}
              </section>
            );

          default:
            return null;
        }
      })}

      {/* Footer */}
      {footer && (
        <footer className="template-footer">
          <div className="container">
            {footer.text && (
              <p className="footer-text">{footer.text}</p>
            )}
            {footer.links && (
              <nav className="footer-nav">
                {footer.links.map((link, index) => (
                  <Link key={index} to={link.href} className="footer-link">
                    {link.label}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};

export default LayoutTemplate;